import { FC, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { useNavigate } from 'react-router-dom';

import {
  GetDisplayPresentationsResponse,
  useGetDisplayPresentations,
  useLoginWithPinCode,
} from '../../../api-http/displays';
import { AlertBar } from '../../../components/AlertBar';
import MiniLogo from '../../../components/sidebar/MiniLogo';
import { useAuth } from '../../../context/auth-context';
import { saveDisplayToken } from '../../../util/cacheStorage';
import { metaTitle } from '../../../util/tabTitle';

const DisplayList: FC = () => {
  metaTitle('Presentation List');

  const { pinCode, setPinCode } = useAuth();
  const { isLoading, mutate, error, data } = useLoginWithPinCode();
  const {
    isLoading: getPresentationIsLoading,
    error: getPresentationError,
    data: getPresentationData,
  } = useGetDisplayPresentations();
  const navigate = useNavigate();
  const [presentationsList, setPresentationsList] = useState<
    GetDisplayPresentationsResponse[]
  >([]);

  useEffect(() => {
    if (pinCode) mutate({ pincode: pinCode });
    else navigate('/display-login');
  }, [pinCode]);

  useEffect(() => {
    if (data) saveDisplayToken(data.authToken || '');
  }, [data]);

  useEffect(() => {
    if (getPresentationData) setPresentationsList(getPresentationData);
  }, [getPresentationData]);

  if (error || getPresentationError) {
    return (
      <AlertBar
        severity="error"
        msg={error?.msg || getPresentationError?.msg}
      />
    );
  }

  if (isLoading || getPresentationIsLoading) return <LinearProgress />;

  const logout = () => {
    setPinCode('');
    navigate('/display-login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef4ff' }}>
      <Paper
        square
        elevation={0}
        sx={{ borderBottom: '1px solid #dbe3ee', bgcolor: '#fff' }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ minHeight: 72 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 42, height: 42 }}>
                <MiniLogo />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                Presentation display
              </Typography>
            </Stack>
            <IconButton
              aria-label="Sign out"
              onClick={logout}
              sx={{ border: '1px solid #dbe3ee', borderRadius: 2 }}
            >
              <PowerSettingsNewIcon color="primary" />
            </IconButton>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography component="h1" sx={{ fontSize: 32, fontWeight: 700 }}>
          Choose a presentation
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75, mb: 3 }}>
          Select the content you want this display to play.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {presentationsList.map(({ name, _id, slides }) => (
            <Card
              key={_id}
              variant="outlined"
              sx={{
                borderColor: '#dbe3ee',
                borderRadius: 3,
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 14px 32px rgba(15, 23, 42, 0.10)',
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(`/presentation-public-preview/${_id}`)}
                sx={{ p: 2.5, minHeight: 170 }}
              >
                <Stack height="100%" justifyContent="space-between" spacing={3}>
                  <Stack direction="row" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        bgcolor: '#e8f5fc',
                        color: '#0b8fca',
                      }}
                    >
                      <SlideshowOutlinedIcon />
                    </Box>
                    <PlayArrowRoundedIcon color="primary" />
                  </Stack>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                      {name}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${slides.length} ${
                        slides.length === 1 ? 'slide' : 'slides'
                      }`}
                      sx={{ mt: 1, bgcolor: '#f2efff', color: '#312e81' }}
                    />
                  </Box>
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default DisplayList;
